/**
 * pages/admin-shared.ts —— 管理后台三个 Tab 共用的两件小事
 *
 * 单独成文件是为了避免 `admin.ts`（壳）与两个 Tab 模块之间的循环引用：
 * Tab 模块要用 `afterWrite`，而壳要 import 两个 Tab 模块。
 */
import { toast } from '../shared/common.js';
import { esc } from '../shared/dom.js';
import * as meta from '../shared/meta.js';
import { refreshCurrent } from '../router/render.js';
import { openModal } from '../shared/common.js';

/**
 * 写操作后的统一收尾：刷 meta（守卫要用）→ 就地重渲染 → 轻提示。
 * ⚠ `meta.refresh()` 不能省：动过队伍 / 周期之后守卫还拿着旧列表，
 *   表现是「刚建的东西点不进去」（shared/meta.ts 纪律 2）。
 * ⚠ 用 `refreshCurrent()` 而不是整页 render：不闪骨架、不丢滚动位置。
 */
export async function afterWrite(msg: string): Promise<void> {
  await meta.refresh();
  refreshCurrent();
  toast(msg);
}

/**
 * 初始密码回显弹窗（§五 5.1 / 5.3 补充 C2）。
 * ⚠⚠ 这串明文**只在那一次响应里出现**：不入库、不写日志、不发通知。
 *   关掉这个弹窗就再也看不到了，所以文案必须把这件事讲死，并且**不自动关闭**。
 * ⚠ 不做「复制到剪贴板」：内网 http 环境下 `navigator.clipboard` 直接不可用，
 *   给一个点了没反应的按钮比不给更糟。输入框是只读的，双击即可全选。
 */
export function showInitialPassword(name: string, password: string, isReset = false): void {
  openModal({
    title: isReset ? '密码已重置' : '账号已创建',
    size: 'sm',
    body: `
      <p class="mtext"><b>${esc(name)}</b> 的${isReset ? '新' : ''}初始密码：</p>
      <div class="fld" style="margin-top:12px">
        <input type="text" readonly value="${esc(password)}"
               style="font-family:Arial,sans-serif;font-size:18px;letter-spacing:1px;text-align:center"
               aria-label="初始密码">
        <div class="hint"><b>请立刻抄走并当面告知本人。</b>关闭本窗口后无法再次查看——
          忘记了只能重新点一次「重置密码」。请提醒本人登录后尽快在登录页的「修改密码」入口自行修改。</div>
      </div>`,
    footer: '<button class="btn primary" data-mclose>我已记录</button>',
    onMount(root) {
      // 打开即全选，管理员按 Ctrl+C 就能带走（不写 inline onfocus：那是唯一一处内联 JS）
      root.querySelector<HTMLInputElement>('input')?.select();
    },
  });
}
